import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuthStore } from '../stores/authStore';
import { api } from '../lib/api';
import { trackConversion, trackError } from '../lib/analytics';
import { Eye, EyeOff, Check } from 'lucide-react';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const campaignCoupon = searchParams.get('coupon')?.trim();
  const isEnterpriseCampaign = searchParams.get('ref') === 'enterprise-campaign';

  const passwordRequirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { met: /[a-z]/.test(password), text: 'One lowercase letter' },
    { met: /[0-9]/.test(password), text: 'One number' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validate password
    if (!passwordRequirements.every(r => r.met)) {
      setError('Please meet all password requirements');
      return;
    }
    
    setIsLoading(true);

    try {
      const response = await api.register(email, password, name || undefined);
      
      if (response.success && response.data) {
        setAuth(
          response.data.user,
          response.data.access_token,
          response.data.refresh_token
        );

        let freePages = 0;
        if (campaignCoupon) {
          try {
            const trialResult = await api.redeemTrialCode(campaignCoupon);
            freePages = trialResult.data?.pages_granted || 0;
            trackConversion('enterprise_trial_redeemed', {
              method: 'signup',
              coupon: campaignCoupon.toUpperCase(),
              pages_granted: freePages,
            });
          } catch (trialError) {
            trackError('enterprise_trial_redeem_failed', {
              method: 'signup',
              coupon: campaignCoupon.toUpperCase(),
              message: trialError instanceof Error ? trialError.message : 'Trial redemption failed',
            });
          }
        } else if (isEnterpriseCampaign) {
          try {
            const campaignResult = await api.redeemEnterpriseCampaign();
            freePages = campaignResult.data?.pages_granted || 0;
            trackConversion('enterprise_campaign_redeemed', {
              method: 'signup',
              pages_granted: freePages,
            });
          } catch (campaignError) {
            trackError('enterprise_campaign_redeem_failed', {
              method: 'signup',
              message: campaignError instanceof Error ? campaignError.message : 'Campaign redemption failed',
            });
          }
        }

        trackConversion('signup_completed', {
          method: 'email',
          free_pages: freePages,
          coupon: campaignCoupon?.toUpperCase() || null,
        });

        navigate('/dashboard');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Create Account | PDF Accessibility Remediation | pdfaa.ai</title>
        <meta name="description" content="Create your PDF AA Compliance account to buy page credits, remediate PDFs, and download certificate evidence for WCAG 2.1 AA, PDF/UA-1, and Section 508 workflows." />
        <meta name="keywords" content="PDF accessibility account, sign up PDF remediation, WCAG compliance, PDF accessibility page credits, PDF remediation" />
        <link rel="canonical" href="https://pdfaa.ai/register" />
        <meta property="og:site_name" content="PDF AA Compliance" />
        <meta property="og:title" content="Create Account | PDF Accessibility Remediation" />
        <meta property="og:description" content="Create an account to buy page credits and remediate PDFs with certificate evidence." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pdfaa.ai/register" />
        <meta property="og:image" content="https://pdfaa.ai/og-image.png" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Create Account | pdfaa.ai" />
        <meta name="twitter:description" content="Buy page credits and remediate PDFs with certificate evidence." />
        <meta name="twitter:image" content="https://pdfaa.ai/og-image.png" />
      </Helmet>
    <div className="min-h-[calc(100vh-12rem)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img 
            src="/logo.png" 
            alt="PDF AA Pro - Document Remediation Service" 
            className="mx-auto h-16 w-auto"
          />
          <h2 className="mt-6 text-3xl font-extrabold text-white">
            Create your account
          </h2>
          <p className="mt-2 text-sm text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-500 hover:text-primary-400">
              Sign in
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg" role="alert">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-300">
                Name (optional)
              </label>
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                Password
              </label>
              <div className="relative mt-1">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-300"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              
              {/* Password requirements */}
              <div className="mt-3 space-y-2">
                {passwordRequirements.map((req, index) => (
                  <div key={index} className="flex items-center text-sm">
                    <Check
                      className={`h-4 w-4 mr-2 ${
                        req.met ? 'text-green-500' : 'text-gray-500'
                      }`}
                    />
                    <span className={req.met ? 'text-green-400' : 'text-gray-400'}>
                      {req.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <input
              id="terms"
              name="terms"
              type="checkbox"
              required
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-600 rounded bg-slate-700 mt-1"
            />
            <label htmlFor="terms" className="ml-2 block text-sm text-gray-300">
              I agree to the{' '}
              <a href="/terms" className="text-primary-500 hover:text-primary-400">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" className="text-primary-500 hover:text-primary-400">
                Privacy Policy
              </a>
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading || !passwordRequirements.every(r => r.met)}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Creating account...' : 'Create account'}
          </button>
          
          <p className="text-center text-sm text-gray-400">
            Buy page credits when you are ready to remediate your first PDF.
          </p>
        </form>
      </div>
    </div>
    </>
  );
}
